import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Feather, Send, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SEASONS } from "../data/basho";

const SEASON_KANJI = { Spring: "春", Summer: "夏", Autumn: "秋", Winter: "冬" };

export default function YourHokku() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [season, setSeason] = useState("Spring");
  const [busy, setBusy] = useState(false);
  const [sendItem, setSendItem] = useState(null);
  const [roomTitle, setRoomTitle] = useState("");
  const [roomSeason, setRoomSeason] = useState("Spring");
  const [sending, setSending] = useState(false);
  const [filterSeason, setFilterSeason] = useState("All");

  const openSend = (h) => {
    const firstLine = h.text.split("\n")[0].slice(0, 40);
    setSendItem(h);
    setRoomTitle(`A renga after "${firstLine}"`);
    setRoomSeason(h.season || "Spring");
  };

  const confirmSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/rooms", {
        title: roomTitle, season: roomSeason, hokku: sendItem.text, theme: "",
      });
      toast.success("Renga room opened with your hokku");
      setSendItem(null);
      navigate(`/rooms/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const load = () => {
    api.get("/hokku").then((res) => setItems(res.data)).catch(() => {});
  };

  useEffect(() => { if (user) load(); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/hokku", { text, season });
      setItems((p) => [data, ...p]);
      setText("");
      toast.success("Hokku inscribed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/hokku/${id}`);
      setItems((p) => p.filter((h) => h.id !== id));
      toast.success("Hokku removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-24 text-muted-foreground">…</div>;

  const filtered = filterSeason === "All" ? items : items.filter((h) => h.season === filterSeason);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="hokku-anon">
        <span className="hanko text-primary text-4xl">発句</span>
        <h1 className="font-serif text-4xl mt-6">Your Hokku</h1>
        <p className="mt-4 text-muted-foreground">Enter the house to compose and keep your own opening verses.</p>
        <Link to="/login" data-testid="hokku-login"
          className="mt-8 inline-block bg-primary text-primary-foreground px-8 py-3 text-sm hover:-translate-y-0.5 transition-transform">
          Enter
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20" data-testid="hokku-page">
      <div className="mb-14 max-w-2xl">
        <span className="hanko text-primary text-2xl">発句</span>
        <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tighter mt-2">Your Hokku</h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          The hokku is the opening breath of a poem — a single moment of season held in seventeen sounds.
          Compose your own here, apart from the renga, and keep them as your private collection.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* composer */}
        <div className="lg:col-span-5">
          <form onSubmit={submit} className="lg:sticky lg:top-24 border border-border p-8" data-testid="hokku-form">
            <div className="flex items-center gap-2 mb-6">
              <Feather className="w-4 h-4 text-primary" />
              <h2 className="font-serif text-2xl">Inscribe a hokku</h2>
            </div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">The verse · 5-7-5</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              required
              data-testid="hokku-input"
              placeholder={"an old silent pond\na frog leaps into the water\nsplash! silence again"}
              className="w-full bg-transparent border border-border px-4 py-3 font-poem text-lg focus:border-primary outline-none transition-colors resize-none"
            />
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 mt-6">Season</label>
            <div className="flex flex-wrap gap-2">
              {SEASONS.map((s) => (
                <button
                  type="button" key={s} onClick={() => setSeason(s)}
                  data-testid={`hokku-season-${s.toLowerCase()}`}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    season === s ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary"
                  }`}
                >
                  {SEASON_KANJI[s]} {s}
                </button>
              ))}
            </div>
            <button
              type="submit" disabled={busy} data-testid="hokku-submit"
              className="mt-8 w-full bg-primary text-primary-foreground py-3 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50"
            >
              {busy ? "Inscribing…" : "Keep this hokku"}
            </button>
          </form>
        </div>

        {/* collection */}
        <div className="lg:col-span-7">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-3xl">Your collection</h2>
            <span className="text-sm text-muted-foreground" data-testid="hokku-count">{filtered.length} verse{filtered.length === 1 ? "" : "s"}</span>
          </div>

          {items.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="hokku-filter">
              {["All", ...SEASONS].map((s) => (
                <button
                  type="button" key={s} onClick={() => setFilterSeason(s)}
                  data-testid={`hokku-filter-${s.toLowerCase()}`}
                  className={`px-3.5 py-1.5 text-sm border transition-colors ${
                    filterSeason === s ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary text-muted-foreground"
                  }`}
                >
                  {s === "All" ? "All" : `${SEASON_KANJI[s]} ${s}`}
                </button>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <div className="border border-border p-16 text-center">
              <p className="font-serif text-2xl text-muted-foreground">Your scroll is empty. Inscribe your first hokku.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-border p-16 text-center" data-testid="hokku-empty-season">
              <p className="font-serif text-2xl text-muted-foreground">No hokku for {filterSeason} yet.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
              {filtered.map((h) => (
                <div key={h.id} className="bg-background p-7 group relative" data-testid={`hokku-item-${h.id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs tracking-widest uppercase text-secondary">
                      {SEASON_KANJI[h.season] || ""} {h.season}
                    </span>
                    <button
                      onClick={() => remove(h.id)}
                      data-testid={`hokku-delete-${h.id}`}
                      aria-label="Delete hokku"
                      className="text-muted-foreground/50 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="font-poem text-xl leading-relaxed whitespace-pre-line">{h.text}</p>
                  <button
                    onClick={() => openSend(h)}
                    data-testid={`hokku-send-${h.id}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-wide text-secondary hover:text-primary transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" /> Send to a Renga Room
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* send-to-renga modal */}
      {sendItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="send-renga-modal">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setSendItem(null)} />
          <form onSubmit={confirmSend} className="relative bg-background border border-border w-full max-w-lg p-6 sm:p-8 md:p-10 max-h-[90vh] overflow-y-auto">
            <button type="button" onClick={() => setSendItem(null)} data-testid="send-renga-close"
              className="absolute top-4 right-4 text-muted-foreground hover:text-primary transition-colors">
              <X className="w-5 h-5" />
            </button>
            <span className="hanko text-primary text-2xl">連歌</span>
            <h3 className="font-serif text-3xl mt-2 mb-2">Open a renga with this hokku</h3>
            <p className="font-poem text-lg text-foreground/80 whitespace-pre-line border-l border-border pl-4 my-5">
              {sendItem.text}
            </p>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Room title</label>
            <input
              value={roomTitle} onChange={(e) => setRoomTitle(e.target.value)} required
              data-testid="send-room-title"
              className="w-full bg-transparent border border-border px-4 py-2.5 focus:border-primary outline-none transition-colors"
            />
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2 mt-5">Season</label>
            <div className="flex flex-wrap gap-2">
              {SEASONS.map((s) => (
                <button
                  type="button" key={s} onClick={() => setRoomSeason(s)}
                  data-testid={`send-season-${s.toLowerCase()}`}
                  className={`px-4 py-2 text-sm border transition-colors ${
                    roomSeason === s ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:border-secondary"
                  }`}
                >
                  {SEASON_KANJI[s]} {s}
                </button>
              ))}
            </div>
            <button type="submit" disabled={sending} data-testid="send-renga-confirm"
              className="mt-8 w-full bg-primary text-primary-foreground py-3 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50">
              {sending ? "Opening the room…" : "Open the renga room"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
